/**
 * Page geometry, in millimetres.
 *
 * THIS IS THE FILE THAT KILLS THE 1.6.x ALIGNMENT BUG.
 *
 * The recurring 1.6.x defect was that vertical borders on a printed invoice
 * did not line up. It was never really a CSS bug - it was the consequence of
 * letting the CONTENT decide the geometry. Content has variable width: a long
 * description widens a column, which moves every border after it, so a cell's
 * left edge stops matching its neighbour's right edge and text overflows past
 * the rule that was drawn for it.
 *
 * The fix is to decide every width ONCE, in millimetres, and let the content
 * do nothing at all. `table-layout: fixed` then means widths come only from
 * `<col>`, and a column can never be widened by what is typed into it.
 *
 * Consequences, all deliberate:
 *  - A column whose content does not fit must WRAP, never widen.
 *  - The totals block is a `<table>` on the same millimetre grid, so it cannot
 *    float away from the item table's right edge - a CSS Grid with a
 *    hand-tuned px floor is exactly what drifted in 1.6.x.
 *  - The item columns, the totals columns and both summary tables each sum to
 *    the SAME printable width, checked at load, so they cannot disagree.
 *
 * A4 is 210mm wide. With a 10mm margin each side, 190mm is printable.
 */
/** A4 width, millimetres. ISO 216. */
export const A4_WIDTH_MM = 210;
/** A4 height, millimetres. */
export const A4_HEIGHT_MM = 297;
/** The margin on all four sides. */
export const PAGE_MARGIN_MM = 10;
/** 210 - 2x10. The one number that governs every column on the page. */
export const PRINTABLE_WIDTH_MM = A4_WIDTH_MM - PAGE_MARGIN_MM * 2;

/** Thermal 58mm and 80mm rolls. Printable width is user-configurable. */
export interface RollGeometry {
  readonly mediaWidthMm: number;
  readonly printableWidthMm: number;
}

export const ROLL_58: RollGeometry = { mediaWidthMm: 58, printableWidthMm: 48 };
export const ROLL_80: RollGeometry = { mediaWidthMm: 80, printableWidthMm: 72 };

/** A set of named column widths, in millimetres. */
export type ColumnSpec = Readonly<Record<string, number>>;

/**
 * The narrowest description column worth printing.
 *
 * Below roughly this, an item name wraps to four or five lines and the table
 * stops being scannable - which is the whole point of the redesign.
 *
 * Declared BEFORE `itemColumns()`, which is called at module load by the
 * `assertColumnsFillPage` loop below. A `const` used before its declaration is
 * a ReferenceError at import time, which fails every suite that touches the
 * renderer rather than one test.
 */
export const MIN_DESCRIPTION_MM = 30;

/**
 * The item table's FIXED column widths, in millimetres. `description` is
 * deliberately absent: it is the elastic one, and `itemColumns()` derives it.
 *
 * DERIVED, NOT HAND-TUNED. The old table carried one literal map summing to
 * 190. Every conditional column then had to be paid for out of that one map,
 * so "hide the HSN column when no line has an HSN" meant remembering to hand
 * 18mm back somewhere, and forgetting left a table 18mm short of the page.
 * Instead each optional column is either present at its own width or absent
 * entirely, and whatever is absent is returned to `description`. The
 * printable width therefore holds by construction rather than by arithmetic
 * somebody has to redo every time a column is added.
 *
 * The widths themselves are unchanged in spirit from the original: `rate`,
 * `taxable`, `tax` and `total` are right-aligned fixed-width numbers, and
 * `total` fits a Rs 17,50,000.00 line without wrapping.
 */
export const ITEM_FIXED_COLUMNS_MM = {
  /** # */
  index: 7,
  /**
   * HSN/SAC.
   *
   * 18mm. This was narrowed to 15mm during the redesign, on the reasoning that
   * codes are short - and the E2E geometry suite immediately measured an
   * eight-digit code (84713010) at 58px of content in a 56px box. Combined with
   * the larger body type, that is an overflow on every invoice whose HSN has
   * eight digits, which is most of them.
   *
   * `doc-ctr` sets `white-space: nowrap`, so this is the one kind of cell that
   * cannot wrap its way out of a narrow column. Widths for the short all-caps
   * columns have to cover the widest realistic value, not the average.
   */
  hsn: 18,
/** Qty */
  quantity: 13,
  /** Qty Ordered (three-way qty) */
  qtyOrdered: 12,
  /** Qty Delivered (three-way qty) */
  qtyDelivered: 12,
  /** Qty Balance (three-way qty) */
  qtyBalance: 12,
  /**
   * Unit.
   *
   * 12mm, not the 10mm this started at. The body font was raised to 9pt, and
   * the E2E geometry suite caught the consequence directly: a three-letter unit
   * measured 58px of content in a 56px box, because `doc-ctr` sets
   * `white-space: nowrap` and cannot wrap its way out. A `nowrap` cell is the one
   * kind of cell that cannot wrap its way out of a narrow column. Widths for the
   * short all-caps columns have to cover the widest realistic value, not the
   * average.
   *
   * The millimetres this takes come back out of `description`, which is derived,
   * so nothing else in the table moves.
   */
  unit: 12,
  /** Rate */
  rate: 21,
  /** Line discount, shown only when a line has one */
  discount: 16,
  /** Taxable value */
  taxable: 23,
  /** Tax (CGST+SGST, or IGST, or cess) */
  tax: 20,
  /** Line total */
  total: 21,
} as const;

/** Every item column, in the order cells are emitted. */
export type ItemColumnKey =
  | 'index'
  | 'description'
  | 'hsn'
  | 'quantity'
  | 'qtyOrdered'
  | 'qtyDelivered'
  | 'qtyBalance'
  | 'unit'
  | 'rate'
  | 'discount'
  | 'taxable'
  | 'tax'
  | 'total';

/** The full set, with `description` filled in: all nine... ten columns. */
export const ITEM_COLUMNS_MM: ColumnSpec = itemColumns({
  showHsn: true,
  showDiscount: true,
  hasThreeWayQty: false,
});

export const ITEM_COL_ORDER: readonly ItemColumnKey[] = itemColumnOrder({
  showHsn: true,
  showDiscount: true,
  hasThreeWayQty: false,
});

/** Which optional columns this document actually needs. */
export interface ItemColumnOptions {
  /** False when no line carries an HSN/SAC. */
  readonly showHsn: boolean;
  /** False when no line carries a discount. */
  readonly showDiscount: boolean;
  /** True for Delivery Note, Purchase Receipt, Sales Return, Purchase Return */
  readonly hasThreeWayQty?: boolean;
  /** Show rate column (false for Delivery Note). */
  readonly showRate?: boolean;
  /** Show taxable value column (false for Delivery Note). */
  readonly showTaxable?: boolean;
  /** Show tax column (false for Delivery Note). */
  readonly showTax?: boolean;
  /** Show total/amount column (false for Delivery Note). */
  readonly showTotal?: boolean;
  /** Show unit column (false for none currently). */
  readonly showUnit?: boolean;
  /** Show quantity column (false for none currently). */
  readonly showQuantity?: boolean;
}

/** The column order for a given set of columns. The order IS the contract. */
export function itemColumnOrder(opt: ItemColumnOptions): readonly ItemColumnKey[] {
  const order: ItemColumnKey[] = ['index', 'description'];
  if (opt.showHsn) order.push('hsn');
  if (opt.hasThreeWayQty) {
    order.push('qtyOrdered', 'qtyDelivered', 'qtyBalance');
  } else if (opt.showQuantity !== false) {
    order.push('quantity');
  }
  if (opt.showUnit !== false) order.push('unit');
  if (opt.showRate !== false) order.push('rate');
  if (opt.showDiscount) order.push('discount');
  if (opt.showTaxable !== false) order.push('taxable');
  if (opt.showTax !== false) order.push('tax');
  if (opt.showTotal !== false) order.push('total');
  return order;
}

/**
 * Build the item column set, deriving `description` so the columns fill the
 * printable width EXACTLY.
 *
 * @throws if the fixed columns alone overflow the page, which would mean the
 *         table cannot be laid out at all rather than merely being cramped.
 */
export function itemColumns(opt: ItemColumnOptions): ColumnSpec {
  const order = itemColumnOrder(opt);
  let fixed = 0;
  for (const key of order) {
    if (key === 'description') continue;
    const w = ITEM_FIXED_COLUMNS_MM[key as keyof typeof ITEM_FIXED_COLUMNS_MM];
    fixed += w;
  }
  const description = PRINTABLE_WIDTH_MM - fixed;
  if (description < MIN_DESCRIPTION_MM) {
    throw new Error(
      `The item columns leave only ${description}mm for the description, which is ` +
        `below the ${MIN_DESCRIPTION_MM}mm floor. Narrow a fixed column rather ` +
        'than shipping an invoice whose item names cannot be read.',
    );
  }
  const columns: Record<string, number> = {};
  for (const key of order) {
    columns[key] = key === 'description' ? description : ITEM_FIXED_COLUMNS_MM[key as keyof typeof ITEM_FIXED_COLUMNS_MM];
  }
  return columns;
}

/**
 * Letterhead columns: seller 62%, buyer 38%.
 *
 * Lives here rather than beside the renderer so it is covered by the same
 * load-time `assertColumnsFillPage` as every other column set. Declaring it in
 * `renderA4.ts` would mean importing it back out of the renderer, and the
 * renderer already imports this module.
 */
export const LETTERHEAD_COLUMNS_MM = {
  seller: 118,
  buyer: 72,
} as const;

/** The totals block. Sums to the same 190 as the item table. */
export const TOTALS_COLUMNS_MM = {
  label: 118,
  value: 72,
} as const;

/**
 * Split a width between N equally-weighted columns.
 *
 * Used for the summary tables, whose TAX LEGS vary by document: a CGST+SGST
 * supply has two leg columns and an IGST supply has one, and both have to end
 * on the same right edge as the item table above them. An earlier version
 * hard-coded all five leg columns and let an intra-state invoice print three
 * of them empty.
 *
 * The remainder is distributed one millimetre at a time rather than rounded
 * into a single column, because `assertColumnsFillPage` requires an exact sum.
 */
export function splitWidth(totalMm: number, parts: number): number[] {
  if (parts <= 0) throw new RangeError(`Cannot split ${totalMm}mm into ${parts} parts.`);
  const base = Math.floor(totalMm / parts);
  const out = new Array<number>(parts).fill(base);
  const remainder = totalMm - base * parts;
  for (let i = 0; i < remainder; i += 1) out[i] = (out[i] ?? 0) + 1;
  return out;
}

/** The sum of a column set, in millimetres. */
export function sumColumns(columns: ColumnSpec): number {
  return Object.values(columns).reduce((s, w) => s + w, 0);
}

/**
 * The invariant every renderer relies on: a column set fills the printable
 * width EXACTLY. Not "approximately" - exactly.
 *
 * This is checked at module load rather than in a test, because a template
 * that is off by a tenth of a millimetre is a permanently detached border,
 * and it should be impossible to even construct one.
 */
export function assertColumnsFillPage(name: string, columns: ColumnSpec): void {
  const sum = sumColumns(columns);
  if (sum !== PRINTABLE_WIDTH_MM) {
    throw new Error(
      `Page geometry is wrong: ${name} sums to ${sum}mm but the printable ` +
        `width is ${PRINTABLE_WIDTH_MM}mm. A column set that does not fill the ` +
        'page exactly cannot produce aligned borders.',
    );
  }
}

/**
 * Which tax legs a document actually carries, and therefore which summary
 * columns it needs.
 *
 * An intra-state supply prints CGST and SGST; an inter-state one prints IGST
 * and nothing else; a cess-only document prints the cess. The renderer decides
 * this from `taxSplit`, which `normalizeDocument` has already derived and
 * validated - it never re-derives it here.
 */
export type TaxLegKey = 'cgst' | 'sgst' | 'igst' | 'cess';

/**
 * The summary columns for a given set of tax legs. Always sums to 190.
 *
 * A document with NO tax legs is legal - a document below the threshold, an
 * exempt supply, a nil-rated line. `splitWidth(…, 0)` throws, so this returns
 * `null` and the caller omits the table rather than reaching for an
 * arithmetic special case. The alternative - inventing a zero-width column, or
 * dividing by zero and producing NaN widths - puts a broken table on a real
 * invoice to avoid a one-line guard.
 */
export function taxSummaryColumns(legs: readonly TaxLegKey[]): ColumnSpec | null {
  if (legs.length === 0) return null;
  // Rate + taxable are fixed; the remainder belongs to the legs.
  const rate = 24;
  const taxable = 46;
  const legWidths = splitWidth(PRINTABLE_WIDTH_MM - rate - taxable, legs.length);
  const columns: Record<string, number> = { rate, taxable };
  legs.forEach((leg, i) => {
    columns[leg] = legWidths[i] ?? 0;
  });
  return columns;
}

/**
 * The HSN summary columns for a given set of tax legs. Always sums to 190, or
 * null when there are no legs - see `taxSummaryColumns`.
 */
export function hsnSummaryColumns(legs: readonly TaxLegKey[]): ColumnSpec | null {
  if (legs.length === 0) return null;
  const hsn = 30;
  const taxable = 44;
  const legWidths = splitWidth(PRINTABLE_WIDTH_MM - hsn - taxable, legs.length);
  const columns: Record<string, number> = { hsn, taxable };
  legs.forEach((leg, i) => {
    columns[leg] = legWidths[i] ?? 0;
  });
  return columns;
}

/**
 * EVERY column set the renderer can emit, asserted at module load.
 *
 * The item table is checked for all four column combinations rather than only
 * the widest one, because the conditional columns are the whole reason the
 * geometry is now derived: a set that is 15mm short when HSN is hidden is a
 * right edge that no longer lines up, and the only moment that is cheap to
 * catch is before anything has rendered.
 */
/**
 * Assert that a summary column set is either a valid page-filling set or absent.
 *
 * The "absent" case is a first-class outcome, not an error: a document with no
 * tax legs has no rate-wise table to lay out, and saying so is the only correct
 * answer. Asserting it here means the null path is checked at import time
 * rather than discovered as a `RangeError` on a user's screen.
 */
export function assertNoSummaryColumns(name: string, columns: ColumnSpec | null): void {
  if (columns !== null) {
    throw new Error(
      `${name} was expected to have no columns, since the document carries no ` +
        'tax legs. A width set here would divide the remaining width between ' +
        'nothing.',
    );
  }
}

/**
 * Every column set the renderer can emit, asserted at module load.
 *
 * The item table is checked for all four column combinations rather than only
 * the widest one, because the conditional columns are the whole reason the
 * geometry is now derived: a set that is 15mm short when HSN is hidden is a
 * right edge that no longer lines up, and the only moment that is cheap to
 * catch is before anything has rendered.
 */
for (const showHsn of [true, false]) {
  for (const showDiscount of [true, false]) {
    assertColumnsFillPage(
      `ITEM(hsn=${showHsn},discount=${showDiscount})`,
      itemColumns({ showHsn, showDiscount, hasThreeWayQty: false }),
    );
  }
}

for (const [name, cols] of Object.entries({
  TOTALS_COLUMNS_MM,
  LETTERHEAD_COLUMNS_MM,
})) {
  assertColumnsFillPage(name, cols);
}

/**
 * Assert a summary column set actually fills the page.
 *
 * A helper rather than `assertColumnsFillPage(…, taxSummaryColumns(legs)!)`
 * because the summary builders return `ColumnSpec | null` and this lint config
 * forbids non-null assertions. Reaching for `!` here would be the easy way to
 * make the compiler quiet, and it would throw a bare `undefined` deep inside
 * `sumColumns` the first time a leg list were ever empty - which is exactly the
 * bug this file already had once.
 */
function assertSummaryFillsPage(name: string, columns: ColumnSpec | null): void {
  if (columns === null) {
    throw new Error(
      `${name} produced no columns, but was expected to fill the page. The leg ` +
        'list passed to it was empty, so there was nothing to divide the width between.',
    );
  }
  assertColumnsFillPage(name, columns);
}

for (const legs of [
  ['cgst', 'sgst'],
  ['igst'],
  ['cgst', 'sgst', 'cess'],
  ['cess'],
] as const) {
  assertSummaryFillsPage(`TAX_SUMMARY(${legs.join('+')})`, taxSummaryColumns([...legs]));
  assertSummaryFillsPage(`HSN_SUMMARY(${legs.join('+')})`, hsnSummaryColumns([...legs]));
}

// And the empty case is asserted too: a nil-rated or exempt document must be
// told "there are no columns" rather than handed a width set that throws.
assertNoSummaryColumns('TAX_SUMMARY(none)', taxSummaryColumns([]));
assertNoSummaryColumns('HSN_SUMMARY(none)', hsnSummaryColumns([]));

/** `colgroup` markup for a column set, e.g. `8mm,62mm,...`. */
export function colgroupMm(columns: ColumnSpec, order: readonly string[]): string {
  return order
    .map((key) => {
      const w = columns[key];
      if (w === undefined) throw new Error(`Unknown column "${key}"`);
      return `<col style="width:${w}mm">`;
    })
    .join('');
}
